// HR Maeve mode chat surface — DESIGN §5.6.1, §10.1.1, Slice 11.
//
// Multi-turn conversation with the HR Maeve mode endpoint (Slice 10
// backend). Server is stateless — the conversation history lives in this
// component's state and is POSTed in full on each turn. History is lost
// on refresh; HR Maeve is a structured-retrieval reference surface, not
// a persistent companion, so session-only state is the right shape.
//
// Closest pattern is `ManagerMaeveGuidance`. Differences from it:
//   - No `caseId` — HR mode is org-wide (§1.2 HR visibility).
//   - No disclosure gate (HR users act professionally; the AI_Disclosure
//     is the bereaved employee's protection, not theirs).
//   - No red-flag handoff (HR mode has no classifier pipeline — DESIGN
//     §10.1.1 #7).
//   - Empty state hints at the four §10.1.1 context blocks so HR users
//     understand what the surface can answer (policy questions, active
//     case overview, anonymised wellbeing aggregates, the HR roster) and
//     what it can't (Employee Companion or Manager conversation content —
//     §10.1.1 #4, #5).
//   - Successful turn writes a `maeve_interactions` audit row server-side
//     (Slice 10) — the Slice 12 governance UI will read those rows. The
//     client does not see audit metadata.

import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { GuidanceTurn, HrMaeveQueryResponse } from '@besideu/shared/maeve';
import { api, ApiError } from '@/lib/api.ts';
import { useScrollToEnd } from '@/lib/use-scroll-to-end.ts';
import { Button } from './ui/Button.tsx';
import { ChatBubble, ChatPendingBubble } from './chat/ChatBubble.tsx';

interface AskResponse extends HrMaeveQueryResponse {
  promptVersionId: string;
}

export function HrMaeveChat() {
  const [history, setHistory] = useState<GuidanceTurn[]>([]);
  const [draft, setDraft] = useState('');

  // Memoise the latest-assistant text so the `aria-live` region's children
  // are referentially stable across unrelated re-renders (TanStack Query
  // background refetches in sibling components, etc.). React reconciles
  // identical text-node children without mutating the DOM — so an AT
  // re-announcement is unlikely either way — but the memo makes the intent
  // ("this string changes only when `history` mutates") load-bearing rather
  // than a side-effect of React's reconciler behaviour.
  const lastMaeveReplyForAt = useMemo(() => {
    const lastAssistant = [...history].reverse().find((t) => t.role === 'assistant');
    return lastAssistant ? `Maeve replied: ${lastAssistant.text}` : '';
  }, [history]);

  const ask = useMutation({
    mutationFn: async (text: string): Promise<{ user: GuidanceTurn; assistant: GuidanceTurn }> => {
      const userTurn: GuidanceTurn = { role: 'user', text };
      const messages = [...history, userTurn];
      const resp = await api.post<AskResponse>('/api/hr/maeve/query', { messages });
      const assistantTurn: GuidanceTurn = { role: 'assistant', text: resp.message };
      return { user: userTurn, assistant: assistantTurn };
    },
    onSuccess: ({ user, assistant }) => {
      setHistory((prev) => [...prev, user, assistant]);
      setDraft('');
    },
  });

  function submit() {
    const trimmed = draft.trim();
    if (trimmed.length === 0 || ask.isPending) return;
    ask.mutate(trimmed);
  }

  const endRef = useScrollToEnd(history.length, ask.isPending);

  return (
    <section className="flex h-full flex-col bg-card">
      {/* Below `md` this is the page's ONLY heading: `HrAskMaeve`'s `<h1>` is
          `hidden md:block` and the app shell has no per-page top bar there.
          At `md`+ that `<h1>` — same words, same scope sentence — sits
          directly above this card, so the title and the "structured questions
          about your organisation: policy / pathways / trends / HR team" line
          were duplicate chrome (PO 2026-08-12); the page header keeps them and
          they drop out here.

          What stays at EVERY width is the advisory + visibility caption. It is
          said nowhere else, and it belongs beside the transcript rather than in
          page furniture: "Maeve cannot see employee or manager conversations"
          is the §10.1.1 #4/#5 boundary an HR user needs while they're asking,
          not something to read once on the way in. */}
      <header className="shrink-0 border-b border-border px-6 py-4">
        <h2 className="text-base font-semibold md:hidden">Ask Maeve</h2>
        <p className="mt-1 text-sm text-muted-foreground md:mt-0">
          Maeve is advisory — confirm with the relevant people before acting. Maeve cannot see
          employee or manager conversations.
        </p>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        {/* `|| ask.isPending`: the very first question in a session has
            `history.length === 0` right up until the reply lands (history only
            grows `onSuccess`), so without this the pending indicator would
            never show for that first question — the EmptyState would keep
            rendering underneath it. */}
        {history.length === 0 && !ask.isPending ? (
          <EmptyState />
        ) : (
          <ChatTranscript history={history} isPending={ask.isPending} />
        )}

        {ask.error ? (
          <div className="mt-3">
            <AskError error={ask.error} onDismiss={() => ask.reset()} />
          </div>
        ) : null}
        <div ref={endRef} />
      </div>

      {/* Screen-reader-only live region carrying ONLY Maeve's latest reply.
          Wrapping the whole `<ol>` in `aria-live` would re-announce the user's
          own turn alongside the response (their words are appended to the same
          list); a separate live region with just the assistant text avoids
          that echo. `polite` rather than `assertive` because a Maeve reply
          is not interruption-worthy — WCAG 2.1 AA 4.1.3. */}
      <div aria-live="polite" className="sr-only">
        {lastMaeveReplyForAt}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="shrink-0 space-y-2 border-t border-border px-6 py-4"
      >
        <label className="block">
          <span className="sr-only">Your question</span>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              // Cmd/Ctrl+Enter submits — mirrors Manager Sub-mode B and the
              // Companion chat. Plain Enter inserts a newline.
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="What does the policy say about bereavement leave for an aunt?"
            // text-base = 16px to avoid iOS Safari zoom on focus.
            className="block w-full resize-y rounded-md border border-border bg-background p-3 text-base disabled:opacity-60"
            rows={3}
            maxLength={4000}
            disabled={ask.isPending}
          />
        </label>
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* `role="status"` so AT announces transitions like
              "Press Cmd/Ctrl + Enter to ask." → "1 previous question in this
              session." — implicit `aria-live="polite"`, no interruption. The
              Maeve-reply live region above carries the response content; this
              one is the lightweight "your turn registered" confirmation. */}
          <p role="status" className="text-xs text-muted-foreground">
            {(() => {
              // `history` only mutates in user+assistant pairs; the floor is
              // a defensive guard against future shapes.
              const questions = Math.floor(history.length / 2);
              if (questions === 0) return 'Press Cmd/Ctrl + Enter to ask.';
              return `${questions} previous ${questions === 1 ? 'question' : 'questions'} in this session.`;
            })()}
          </p>
          <Button
            type="submit"
            loading={ask.isPending}
            disabled={draft.trim().length === 0 || ask.isPending}
            className="py-2 text-sm"
          >
            Ask Maeve
          </Button>
        </div>
      </form>
    </section>
  );
}

function EmptyState() {
  // Worth showing: the four §10.1.1 context blocks framed as user-facing
  // capabilities. Not worth showing: the §6.4 min-pool suppression rule —
  // an HR user does not need to understand the internal aggregation
  // threshold to use the surface, and exposing "below the pool threshold"
  // as a concept invites questions Maeve refuses to answer by design.
  return (
    <div className="rounded-md border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground">
      <p className="mb-2 font-medium text-foreground">A few things Maeve can help with:</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>Bereavement policy questions — Maeve cites the policy section.</li>
        <li>An overview of your organisation's active support pathways and where they're up to.</li>
        <li>Anonymised wellbeing patterns across the org (never individual identification).</li>
        <li>Who's on your HR team and what each person is responsible for.</li>
      </ul>
    </div>
  );
}

function ChatTranscript({ history, isPending }: { history: GuidanceTurn[]; isPending: boolean }) {
  return (
    <ol className="space-y-3" aria-label="Conversation with Maeve">
      {history.map((turn, idx) => (
        // History is append-only within this component lifetime, so index
        // is a stable key. (Resetting state remounts the list, which is fine.)
        <li key={idx}>
          <ChatBubble role={turn.role}>{turn.text}</ChatBubble>
        </li>
      ))}
      {isPending ? (
        <li>
          <ChatPendingBubble />
        </li>
      ) : null}
    </ol>
  );
}

// HR-mode error matrix (from `apps/api/src/routes/hr.ts`):
//   503 maeve_unavailable      — no active HR_Maeve prompt loaded (operator must seed)
//   502 maeve_upstream_error   — Vertex flake or policy R2 transient outage
//   400 invalid_body / last_message_must_be_user — schema validation
//
// Schema-validation 400s should be unreachable from this UI (the textarea
// length cap matches the Zod max and the form always submits a user turn),
// but the generic-error fallback below catches them if a future change
// allows them through.
function AskError({ error, onDismiss }: { error: unknown; onDismiss: () => void }) {
  const { headline, detail } = explainError(error);
  return (
    // `role="status"` (implicit `aria-live="polite"`) so screen readers
    // announce the failure when the card mounts — WCAG 2.1 AA 4.1.3 (Status
    // Messages). NOT `role="alert"` / `aria-live="assertive"`: a "couldn't
    // reach Maeve" network error is recoverable and low-stakes; the user
    // has already stopped to wait for a response, so a polite announcement
    // won't be missed. Assertive is reserved for genuinely urgent events
    // (e.g. the Companion-mode crisis handoff in `RedFlagHandoff`).
    <div
      role="status"
      className="space-y-3 rounded-md border border-traffic-red/40 bg-traffic-red/5 p-4 text-sm"
    >
      <div>
        <p className="font-medium text-traffic-red">{headline}</p>
        <p className="mt-1 text-muted-foreground">{detail}</p>
      </div>
      <Button variant="secondary" onClick={onDismiss} className="py-1.5 text-sm">
        Dismiss
      </Button>
    </div>
  );
}

function explainError(error: unknown): { headline: string; detail: string } {
  if (error instanceof ApiError) {
    if (error.status === 503 && error.message === 'maeve_unavailable') {
      return {
        headline: "Maeve isn't available right now",
        detail:
          'A besideU administrator needs to enable the HR prompt before Maeve can answer questions. The governance team has been notified.',
      };
    }
    if (error.status === 502 && error.message === 'maeve_upstream_error') {
      return {
        headline: "Couldn't reach Maeve",
        detail:
          'The service is temporarily unavailable. Your question wasn’t sent — please try again in a moment.',
      };
    }
  }
  return {
    headline: 'Something went wrong',
    detail: 'Please try again, or refresh the page if the problem persists.',
  };
}
